"use client";

import { useRouter } from "next/navigation";
import React, { ReactElement, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import useGtmPageView from "@/app/common-hooks/gtm/use-gtm-page-view";
import useCustomPageTracking from "@/app/common-hooks/page-tracking/use-custom-page-tracking";
import HelpOverlayButton from "@/app/shared-components/help-overlay-button/help-overlay-button-wizard";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import Logo from "@/components/molecules/logo-component/logo";
import AMTPaymentSelectorWrapper from "@/components/molecules/quote-section/amt-payment-selector/amt-payment-selector-wrapper";
import { FooterRenewal } from "@/components/molecules/renewals/footer/renewal-footer";
import CardholderConsentModal from "@/components/molecules/renewals/payment-summary/cardholder-consent/cardholder-consent-modal";
import { PaymentFailedModal } from "@/components/organisms/renewals/payment-summary/payment-faild-modal/payment-failed-modal";
import { PriceSummarySection } from "@/components/organisms/renewals/payment-summary/price-summary/price-summary-section";
import UnSavedChangesWarningMessage from "@/components/organisms/un-saved-changes-warning-message/un-saved-changes-warning-message";
import withQuoteData from "@/components/with-quote-data";
import { RenewalPaymentSummary } from "@/config/app-constant";
import { usesaveConsent } from "@/hooks/api/review-api/use-save-cardholder-consent";
import { useSubmitReview } from "@/hooks/api/review-api/use-submit-review";
import useGtmViewQuoteData from "@/hooks/gtm/use-gtm-view-quote";
import useMediaQuery from "@/hooks/use-media-query/use-media-query-enhanced";
import { useLoading } from "@/providers/loading-provider";
import { useRenewalPaymentSummaryStateContext } from "@/providers/renewal-payment-summary-auto-pay-state-provider";
import {
  PriceSummarySectionSchemaType,
  PaymentFailedModalSchemaType,
  CardholderCosentModalType,
} from "@/schema/renewal-page/payment-summary-page.schema";
import { RootState } from "@/store/root-reducer";
import { updateQuote } from "@/store/slices/quote/quote.slice";
import getFullPageRelativeTop from "@/utils/get-related-page-top";
import QuoteDataMap from "@/utils/gtm/quote-data-map";

interface PclSectionProps {
  priceResultsTitle: string;
  monthlyPaySummaryTitle: string;
  priceSummaryDescription: string;
  policyPriceRow: string;
  totalCreditChargeRow: string;
  intrestRow: string;
  fixedInterestRateLabel: string;
  initialPaymentRow: string;
  installmentsRow: string;
  totalPriceRow: string;
  notAvailableText: string;
  interestRowMobile?: string;
}

interface PaymentSummaryWrapperProps {
  pclSectionContent: PclSectionProps;
  priceSummarySectionContent: PriceSummarySectionSchemaType;
  promoCodeSection: ReactElement;
  autoPaySection: ReactElement;
  cardholderConsentModalContent: CardholderCosentModalType;
  paymentFailedModalContent: PaymentFailedModalSchemaType;
}

function PaymentSummaryWrapper({
  pclSectionContent,
  priceSummarySectionContent,
  promoCodeSection,
  autoPaySection,
  cardholderConsentModalContent,
  paymentFailedModalContent,
}: Readonly<PaymentSummaryWrapperProps>) {
  const router = useRouter();
  const dispatch = useDispatch();
  const { current } = useWizard();
  const { attributes, referenceId, version, type } = useSelector(
    (state: RootState) => state.quote
  );
  const payOption = useSelector((state: RootState) => state.paymentConsents);
  const promoSectionRef = React.useRef<HTMLDivElement>(null);
  const [isdesktop, isReady] = useMediaQuery("(min-width: 600px)");
  const [isLargeScreen, _] = useMediaQuery("(min-width: 904px)");
  const [isShowPromoCode, setIsShowPromoCode] = React.useState(false);
  const [isOpenModal, setIsOpenModal] = React.useState(false);
  const [buttonDisabled, setButtonDisabled] = React.useState(false);
  const [shouldTriggerGtm, setShouldTriggerGtm] = React.useState(false);
  const { setIsOpenCardholderConsentModal, autoPaySelected } =
    useRenewalPaymentSummaryStateContext();

  const { hideLoading, showLoading } = useLoading();
  const [newQuote, submitReview] = useSubmitReview(
    {
      version,
      type,
      attributes,
      referenceId,
      viewPage: "QR",
    },
    setButtonDisabled
  );
  const [saveConsent] = usesaveConsent({
    version,
    type,
    attributes,
    referenceId,
  });

  const { pageUrl, pageReferrer, pageTitle } = useCustomPageTracking(
    RenewalPaymentSummary
  );

  useGtmPageView({
    userId: "",
    quoteId: "",
    pageReferrer,
    pageUrl,
    pageTitle,
  });

  const callReviewSubmit = async () => {
    setButtonDisabled(true);
    setShouldTriggerGtm(true);
    if (typeof submitReview === "function") {
      await submitReview();
    }
  };

  const callReviewSubmitWithConsent = async () => {
    setButtonDisabled(true);
    const isSaved = await saveConsent();
    if (isSaved) {
      callReviewSubmit();
    } else {
      setButtonDisabled(false);
    }
  };

  const handleSubmitReview = async () => {
    if (
      !autoPaySelected &&
      attributes.paymentDetails?.paymentOptions[0]?.cardPayment?.card
        ?.totalPrice === "0.00" &&
      !(
        attributes.mtaMedicalConsent?.isTravellingBeforeRenewal &&
        attributes.paymentDetails?.paymentOptions[0]?.cardPayment?.card
          ?.mtaPrice &&
        attributes.paymentDetails?.paymentOptions[0]?.cardPayment?.card
          ?.mtaPrice !== "0.00"
      )
    ) {
      callReviewSubmit();
      return;
    }
    if (!autoPaySelected) {
      setIsOpenCardholderConsentModal(true);
      return;
    }
    await callReviewSubmit();
  };

  useEffect(() => {
    if (newQuote) {
      if (newQuote && typeof newQuote !== "function") {
        dispatch(updateQuote(newQuote));
        window.location.href = current.next ?? "";
      }
    }
    if (buttonDisabled) {
      showLoading();
    } else {
      hideLoading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, newQuote, buttonDisabled]);

  let gtmQuoteViewData = null;
  if (referenceId) {
    gtmQuoteViewData = QuoteDataMap(
      attributes,
      "renewal_view_cart",
      "renewal_summary",
      referenceId,
      "renewal_journey"
    );
  }

  useGtmViewQuoteData({
    quoteData: gtmQuoteViewData,
    isReady: (referenceId as unknown as boolean) && isReady,
  });

  let gtmQuoteViewData2 = null;
  if (referenceId) {
    gtmQuoteViewData2 = QuoteDataMap(
      attributes,
      "renewals_add_payment_info",
      "renewal_payment",
      referenceId,
      "renewal_journey"
    );
  }

  useGtmViewQuoteData({
    quoteData: {
      ...gtmQuoteViewData2,
      renewal_transaction_id: null,
      renewal_payment_method: null,
      voucher_code: attributes.paymentDetails?.discounts.voucher ?? null,
      currency: "GBP",
      auto_renewal: !attributes.paymentDetails?.optOutNextYearAutoRenewal,
    },
    isReady: shouldTriggerGtm,
  });

  const handleGotAPromoOrVoucherCode = () => {
    setIsShowPromoCode(true);
    setTimeout(() => {
      const topOffset = getFullPageRelativeTop(
        promoSectionRef?.current as HTMLElement
      );
      const elementHeight = promoSectionRef?.current?.offsetHeight as number;
      window.scrollTo({ top: topOffset + elementHeight, behavior: "smooth" });
    }, 200);
  };

  if (!referenceId) return null;
  if (!isReady) return null;

  return (
    <div
      data-testid="renewals-review-test-id"
      className="bg-surface-neutral-option-2 "
    >
      <div className="max-w-[1200px] lg:mx-auto md:mx-[48px] mx-[24px] min-h-[calc(100vh-12px)] md:py-spacing-xxl py-spacing-l lg:pb-0 md:pb-spacing-5xl">
        <PaymentFailedModal
          content={paymentFailedModalContent}
          tryAgainSubmit={callReviewSubmit}
        />
        <main className="grid grid-cols-12 lg:gap-spacing-l md:gap-spacing-xl gap-spacing-l md:mb-0 mb-spacing-5xl ">
          <aside className="flex flex-col h-full flex-grow-0  md:gap-spacing-xl gap-spacing-l lg:col-span-7 col-span-12 lg:order-first order-last md:mb-spacing-l lg:mb-0">
            <section className="bg-surface-neutral-option-1 rounded-radius-xxl">
              <div className="lg:flex flex-col">
                <div className="text-surface-neutral-option-1 relative hidden lg:flex">
                  {isLargeScreen && <HelpOverlayButton />}
                  <Logo
                    color="primary"
                    heightClass="h-[80px]"
                    size="xl"
                    showondesktop={isdesktop.toString()}
                  />
                </div>
                <div className="rounded-radius-xxl bg-surface-neutral-option-1">
                  <AMTPaymentSelectorWrapper
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...pclSectionContent}
                    isdesktop={isdesktop.toString()}
                  />
                </div>
              </div>
            </section>
            {/* add a component */}

            <section className="bg-surface-neutral-option-1 rounded-radius-xxl h-fit ">
              <div className="lg:flex flex-col">{autoPaySection}</div>
            </section>
          </aside>
          <aside className="lg:col-span-5 col-span-12 ">
            <section
              ref={promoSectionRef}
              className="rounded-radius-xxl bg-dark-surface-accent-1-primary"
            >
              <div className="flex flex-col h-full lg:hidden">
                <div className="rounded-radius-xxl text-surface-neutral-option-2 relative">
                  {!isLargeScreen && <HelpOverlayButton />}
                  <Logo
                    color="white"
                    heightClass="h-[80px]"
                    size="xl"
                    showondesktop={isdesktop.toString()}
                  />
                </div>
                <div className="rounded-radius-xxl text-surface-neutral-option-2">
                  <PriceSummarySection
                    content={priceSummarySectionContent}
                    gotAPromoOrVoucherCode={handleGotAPromoOrVoucherCode}
                  />
                </div>
              </div>
              <div className="rounded-radius-xxl lg:block hidden text-surface-neutral-option-2">
                <PriceSummarySection
                  content={priceSummarySectionContent}
                  gotAPromoOrVoucherCode={handleGotAPromoOrVoucherCode}
                />
              </div>
            </section>

            {isShowPromoCode && (
              <section className="rounded-radius-xxl bg-surface-neutral-option-1">
                {promoCodeSection}
              </section>
            )}
          </aside>
        </main>
        <footer>
          <FooterRenewal
            footerContinueCta="Continue"
            onClickBack={() => router.push(current.prev ?? "")}
            onClickContinue={handleSubmitReview}
            disableMain={payOption.type === null}
          />
        </footer>
      </div>
      <UnSavedChangesWarningMessage
        isOpen={isOpenModal}
        setIsOpen={setIsOpenModal}
        onContinueClick={() => {
          setIsOpenModal(false);
          router.push("/");
        }}
      />

      <CardholderConsentModal
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...cardholderConsentModalContent}
        handleContinue={callReviewSubmitWithConsent}
      />
    </div>
  );
}

export default withQuoteData(PaymentSummaryWrapper);
